import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Loader2, Package, ShieldCheck, ArrowLeft, MessageSquare } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { usePageMeta } from "@/lib/usePageMeta";
import api from "@/lib/api";

const BE = process.env.REACT_APP_BACKEND_URL || "";
const src = (u) => (u && u.startsWith("/api") ? BE + u : u);

export default function HolidayDestinationPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/travel/holiday-destinations/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const dest = data?.destination;
  usePageMeta(
    dest ? `${dest.name} Holiday Packages · Suffle Online` : "Holiday Destination · Suffle Online",
    dest ? `Holiday packages for ${dest.name} (${dest.country}) — ${(dest.themes || []).join(", ")}. Compare and book verified ${dest.primary} trips on Suffle.` : "Explore holiday packages on Suffle Online."
  );

  return (
    <PublicShell>
      <div className="mx-auto max-w-6xl px-4 py-8" data-testid="holiday-destination-page">
        <Link to="/holidays" className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-fuchsia-600"><ArrowLeft className="h-3.5 w-3.5" /> All destinations</Link>
        {loading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : notFound || !dest ? (
          <p className="rounded-2xl border border-slate-100 p-12 text-center text-sm text-slate-400" data-testid="destination-not-found">Destination not found.</p>
        ) : (
          <>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-fuchsia-600 via-violet-600 to-sky-600 p-6 text-white" data-testid="destination-hero" style={dest.image ? { backgroundImage: `linear-gradient(135deg, rgba(88,15,110,0.55), rgba(20,40,120,0.45)), url("${dest.image}")`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
              <div className="relative min-h-[120px]">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold backdrop-blur"><MapPin className="h-3.5 w-3.5" /> {dest.country}</div>
              <h1 className="mt-2 text-3xl font-bold drop-shadow" data-testid="destination-name">{dest.name}</h1>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(dest.themes || []).map((t) => <span key={t} className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold backdrop-blur">{t}</span>)}
              </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-900">Holiday packages for {dest.primary} <span className="text-sm font-normal text-slate-400">({data.count})</span></h2>
              <Link to="/services?travel_type=package" className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:border-sky-300" data-testid="destination-ask-quote"><MessageSquare className="h-3.5 w-3.5" /> Ask an agent for a custom trip</Link>
            </div>

            {(data.packages || []).length === 0 ? (
              <p className="mt-4 rounded-2xl border border-slate-100 p-10 text-center text-sm text-slate-400" data-testid="destination-empty">No packages listed for {dest.primary} yet — ask an agent above for a custom quote.</p>
            ) : (
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="destination-packages">
                {data.packages.map((s) => { const hh = s.holiday || {}; return (
                  <Link key={s.service_id} to={`/service/${s.slug}`} data-testid={`dest-package-${s.service_id}`} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:shadow-md">
                    <div className="h-36 w-full overflow-hidden bg-slate-100">{(s.images || [])[0] ? <img src={src(s.images[0])} alt="" className="h-full w-full object-cover transition group-hover:scale-105" /> : <div className="flex h-full flex-col items-center justify-center bg-gradient-to-br from-fuchsia-500 via-violet-500 to-sky-500 text-white"><Package className="h-7 w-7" /><span className="mt-1 px-3 text-center text-[11px] font-semibold line-clamp-2">{s.name}</span></div>}</div>
                    <div className="p-3">
                      <p className="text-sm font-bold text-slate-800">{s.name}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500">{(hh.stops || []).slice(0, 3).join(" · ") || hh.start_location || "Holiday package"}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {(hh.days || hh.nights) && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{hh.days || "?"}D/{hh.nights || "?"}N</span>}
                        {(hh.themes || []).slice(0, 2).map((t) => <span key={t} className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-semibold text-fuchsia-600">{t}</span>)}
                      </div>
                      <p className="mt-2 text-sm font-bold text-slate-900">{s.pricing_mode === "quote" ? "On request" : s.pricing_mode === "negotiable" ? "Negotiable" : `${s.pricing_mode === "starting_from" ? "From " : ""}₹${s.price || Number(hh?.pricing?.adult) || 0}`}</p>
                    </div>
                  </Link>
                ); })}
              </div>
            )}
          </>
        )}
      </div>
    </PublicShell>
  );
}
